/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from "react";
import {
  Modal,
  TwoFactorVerification,
  VerificationCodeCheckScreen,
} from "components/common";
import { useMutation } from "@apollo/react-hooks";
import {
  VERIFICATION_CODE_SEND, // with new number
  VERIFICATION_CODE_CHECK, // with update
  REFETCH_USER_PHONE,
} from "./mutations";
import PhoneNumberInputScreen from "./PhoneNumberInputScreen";
import { formatPhoneNumberIDE64 } from "./utils";

const PHONE_VERIFICATION_INCORRECT_MESSAGE = "⚠️ Verification code is incorrect";
const SAME_PHONE_NUMBER_MESSAGE = "Cannot update to the same number";
const SEND_CODE_ERROR_MESSAGE = "Cannot complete your request. Please try again later";
const INVALID_PHONE_NUMBER_MESSAGE = "⚠️ Please enter a valid phone number";
const PHONE_VERIFICATION_INVALID_MESSAGE = "Please enter a valid verification code";
const RESEND_CODE_DELAY_SECOND = 30;
const VERIFICATION_CODE_LENGTH = 5;

const SettingsUpdatePhoneComponent = ({ user, isMobile, handleNext }) => {
  const [newNumberVerificationCode, setNewNumberVerificationCode] = useState("");
  const [identityVerified, setIdentityVerified] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [verifyingNewPhoneNumber, setVerifyingNewPhoneNumber] = useState(false);
  const [newPhoneNumberVerified, setNewPhoneNumberVerified] = useState(false);
  const [message, setMessage] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // function to start the countdown for resend button enablement
  const startResendCountdown = () => {
    setResendDisabled(true);
    setCountdown(RESEND_CODE_DELAY_SECOND);

    const countdownInterval = setInterval(() => {
      setCountdown((prevCountdown) => {
        const newCountdown = prevCountdown - 1;
        if (newCountdown === 0) {
          clearInterval(countdownInterval);
          setResendDisabled(false);
        }
        return newCountdown;
      });
    }, 1000);
  };

  const [verificationCodeSend, { loading: sendCodeLoadingNewNumber }] = useMutation(
    VERIFICATION_CODE_SEND,
  );
  const [verificationCodeCheck, { loading: checkCodeLoadingNewNumber }] = useMutation(
    VERIFICATION_CODE_CHECK,
  );

  const handlePhoneInputChanged = (phoneNumberRaw) => {
    const formatted = formatPhoneNumberIDE64(phoneNumberRaw);
    setNewPhoneNumber(formatted);
  };

  const handleNewPhoneNumberSubmitted = () => {
    if (sendCodeLoadingNewNumber) {
      return;
    }
    if (resendDisabled) {
      return;
    }
    if (!formatPhoneNumberIDE64(newPhoneNumber)) {
      setMessage(INVALID_PHONE_NUMBER_MESSAGE);
      return;
    }
    if (newPhoneNumber === user.phone_number) {
      setMessage(SAME_PHONE_NUMBER_MESSAGE);
      return;
    }
    setMessage("");
    verificationCodeSend({
      variables: {
        phoneNumber: newPhoneNumber,
      },
    })
      .then((res) => {
        startResendCountdown();
        const { status } = res.data?.verificationCodeSend || {};
        if (status === "SUCCESS") {
          setVerifyingNewPhoneNumber(true);
          return;
        }
        setMessage(SEND_CODE_ERROR_MESSAGE);
      })
      .catch((err) => {
        const { message: errorMessage, displayMessage } = err.graphQLErrors?.[0] || {};
        setMessage(displayMessage || errorMessage);
      });
  };

  const handleNewVerificationCodeSubmitted = () => {
    if (checkCodeLoadingNewNumber) {
      return;
    }
    if (newNumberVerificationCode.length !== VERIFICATION_CODE_LENGTH) {
      setMessage(PHONE_VERIFICATION_INVALID_MESSAGE);
      return;
    }
    setMessage("");
    verificationCodeCheck({
      variables: { phoneNumber: newPhoneNumber, code: newNumberVerificationCode },
      refetchQueries: [{ query: REFETCH_USER_PHONE }],
      awaitRefetchQueries: true
    })
      .then((res) => {
        const { status } = res.data?.verificationCodeCheck || {};
        if (status === "SUCCESS") {
          handleNext();
          setNewPhoneNumberVerified(true);
          return;
        }
        // else case, show default message from backend
        setMessage(PHONE_VERIFICATION_INCORRECT_MESSAGE);
      })
      .catch((err) => {
        const { message: errorMessage, displayMessage } = err.graphQLErrors?.[0] || {};
        setMessage(displayMessage || errorMessage);
      });
  };

  // step 1: 2fa
  if (!identityVerified) {
    return (
      <TwoFactorVerification
        user={user}
        isMobile={isMobile}
        verifyMethods={["email", "sms"]}
        onVerified={() => setIdentityVerified(true)}
      />
    );
  }

  // step 2: enter new number
  if (!verifyingNewPhoneNumber) {
    return (
      <>
        <div className={`text-sm mb-4 text-green-primary ${isMobile ? "" : "mx-10"}`}>Great! We wanted to make sure it was you before making changes to your account.</div>
        <PhoneNumberInputScreen
          loading={sendCodeLoadingNewNumber}
          titleText="Change your phone number"
          errorMessage={message}
          phoneNumber={newPhoneNumber}
          setPhoneNumber={handlePhoneInputChanged}
          handleSubmit={handleNewPhoneNumberSubmitted}
        />
      </>
    );
  }

  // step 3: verify new number with code
  return (
    !newPhoneNumberVerified &&
    <>
      <VerificationCodeCheckScreen
        loading={checkCodeLoadingNewNumber}
        errorMessage={message}
        isMobile={isMobile}
        titleText="Verify your mobile number"
        sendToRecipient={newPhoneNumber}
        verificationCode={newNumberVerificationCode}
        setVerificationCode={setNewNumberVerificationCode}
        handleSubmit={handleNewVerificationCodeSubmitted}
      />
      <div className="flex space-x-2 justify-center text-green-primary">
        <button
          type="button"
          className="focus:outline-none text-sm"
          // should implement debounce timer here
          onClick={() => {
            setNewNumberVerificationCode("");
            handleNewPhoneNumberSubmitted();
          }}
        >
          {countdown > 0 ? `Re-send SMS in ${countdown}s` : `Re-send SMS`}
        </button>
      </div>
    </>
  );
};

const SettingsUpdatePhone = ({
  isOpen,
  close = () => {},
  user,
  isMobile,
  handleNext
}) => {
  // if isMobile, render as a full screen div
  // is not mobile, render as a modal
  return isMobile ? (
    <div className="flex flex-col justify-center items-center text-center">
      <SettingsUpdatePhoneComponent user={user} isMobile={isMobile} handleNext={handleNext} />
    </div>
  ) : (
    <Modal
      isOpen={isOpen}
      close={close}
      hasCrossContainer
    >
      <div
        className="p-10 flex flex-col justify-center items-center text-center bg-white"
        style={{ width: 450 }}
      >
        <SettingsUpdatePhoneComponent user={user} isMobile={isMobile} handleNext={handleNext} />
      </div>
    </Modal>
  );
};

export default SettingsUpdatePhone;